import { Eye, Trash2 } from "lucide-react";
import { formatPaymentLogDateTime } from "../../utils/applicationFormatters";
import { formatAmount } from "@/lib/utils/formatters";
import { EditIconButton } from "@/components/ui/EditIconButton";
import { SectionInfoIcon } from "@/components/ui/SectionInfoIcon";
import styles from "../../styles/applicationDetail.module.css";
import {
  computePaymentLogBaseFee,
  computePaymentLogServiceFee,
  paymentLogTransactionDisplay,
  resolvePaymentLogPaidByNotes,
} from "../../utils/signaturePaymentHelpers";
import {
  canDeletePaymentLogRow,
  canEditPaymentLogRow,
  canViewPaymentLogRow,
} from "../../utils/signaturePaymentLogActions";
import type { PaymentLine } from "../../types/tabs";
import {
  formatPaymentStatusLabel,
  paymentStatusBadgeClass,
  shouldShowPaymentLogSection,
} from "./signaturePaymentTabLogic";
import type { SignaturePaymentLogSectionProps } from "../../types";

export function SignaturePaymentLogSection({ tab }: SignaturePaymentLogSectionProps) {
  const {
    applicationAllData,
    applicantId,
    canPerformPaymentActions,
    canEditPayment,
    canDeletePayment,
    deletingPaymentId,
    paymentLogRowsForCurrentApplicant,
    setShowExternalPayment,
    setPaymentDeleteConfirm,
    handleViewPaymentLog,
    handleEditPaymentLog,
    resolveGateway,
  } = tab;

  if (!shouldShowPaymentLogSection(applicationAllData, applicantId)) {
    return null;
  }

  const renderRow = (payment: PaymentLine, index: number, rows: PaymentLine[]) => {
    const gatewayResolved = resolveGateway(
      payment.payment_source,
      payment.gateway_status,
    );
    const gatewayTooltipText =
      gatewayResolved.description ||
      (payment.gateway_status != null ? String(payment.gateway_status) : "");
    const paidByNotes = resolvePaymentLogPaidByNotes(payment);

    return (
      <tr
        key={`${String(payment.payment_log_id ?? "")}-${String(payment.pa_id ?? "")}-${index}`}
        className={!payment.payment_log_id ? styles.highlight : ""}
      >
        <td>{payment.payment_type || "N/A"}</td>
        <td>{payment.payment_source || "N/A"}</td>
        <td className={styles.paymentLogAmountCell}>
          {formatAmount(computePaymentLogBaseFee(payment))}
        </td>
        <td className={styles.paymentLogAmountCell}>
          {formatAmount(computePaymentLogServiceFee(payment))}
        </td>
        <td className={styles.paymentLogAmountCell} style={{ fontWeight: "500" }}>
          {payment.payment_amount ? formatAmount(payment.payment_amount) : "$0.00"}
        </td>
        <td>{payment.payment_log_id || "-"}</td>
        <td>{paymentLogTransactionDisplay(payment)}</td>
        <td>{payment.ip_address || "-"}</td>
        <td>{formatPaymentLogDateTime(payment.created_at)}</td>
        <td>
          <span
            className={`${styles.statusBadge} ${paymentStatusBadgeClass(
              payment.payment_status,
            )}`}
          >
            {formatPaymentStatusLabel(payment.payment_status)}
          </span>
        </td>
        <td>
          <div className={styles.paymentLogGatewayCell}>
            <span className="truncate">{gatewayResolved.display}</span>
            {payment.gateway_status && gatewayTooltipText ? (
              <SectionInfoIcon
                align="start"
                maxWidth={360}
                iconSize={16}
                isLastRow={index === rows.length - 1}
                aria-label="Gateway status details"
                clampPanelHeight={false}
                className="shrink-0"
                contentClassName="px-3 py-2 text-xs text-slate-700 whitespace-pre-wrap"
                content={gatewayTooltipText}
              />
            ) : null}
          </div>
        </td>
        <td>
          <div className={styles.paymentLogGatewayCell}>
            <span>{payment.paid_by || ""}</span>
            {paidByNotes ? (
              <SectionInfoIcon
                align="start"
                maxWidth={360}
                iconSize={16}
                isLastRow={index === rows.length - 1}
                aria-label="Payment notes"
                clampPanelHeight={false}
                className="shrink-0"
                contentClassName="px-3 py-2 text-xs text-slate-700 whitespace-pre-wrap"
                content={paidByNotes}
                data-testid={`application-signature-payment-tab-payment-log-${index}-paid-by-notes`}
              />
            ) : null}
          </div>
        </td>
        {canPerformPaymentActions && (
          <td className={styles.paymentLogActionCell}>
            <div className={styles.paymentLogActionGroup}>
              {canViewPaymentLogRow(payment) && (
                <button
                  type="button"
                  className={styles.paymentLogIconBtn}
                  title="View transaction details"
                  aria-label="View transaction details"
                  data-testid={`application-signature-payment-tab-payment-log-${index}-view-button`}
                  onClick={() => void handleViewPaymentLog(payment)}
                >
                  <Eye className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
              {canEditPayment && canEditPaymentLogRow(payment) && (
                <EditIconButton
                  className={styles.paymentLogIconBtn}
                  title="Edit payment"
                  aria-label="Edit payment"
                  iconClassName="h-3.5 w-3.5"
                  data-testid={`application-signature-payment-tab-payment-log-${index}-edit-button`}
                  onClick={() => handleEditPaymentLog(payment)}
                />
              )}
              {canDeletePayment && canDeletePaymentLogRow(payment) && (
                <button
                  type="button"
                  className={`${styles.paymentLogIconBtn} ${styles.paymentLogIconBtnDanger}`}
                  title="Delete payment adjustment"
                  aria-label="Delete payment adjustment"
                  disabled={
                    deletingPaymentId != null &&
                    (deletingPaymentId === payment.payment_log_id ||
                      deletingPaymentId === payment.pa_id)
                  }
                  data-testid={`application-signature-payment-tab-payment-log-${index}-delete-button`}
                  onClick={() => setPaymentDeleteConfirm({ open: true, payment })}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
            </div>
          </td>
        )}
      </tr>
    );
  };

  return (
    <div
      className={styles.section}
      data-testid="application-signature-payment-tab-section-payment-log"
    >
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Payment Log</h2>
        {canEditPayment && (
          <EditIconButton
            className="rounded border border-gray-200"
            iconClassName="h-3 w-3"
            title="Add External Payment"
            aria-label="Add External Payment"
            data-testid="application-signature-payment-tab-add-external-payment-button"
            onClick={() => setShowExternalPayment(true)}
          />
        )}
      </div>

      <div className={styles.tableResponsive}>
        <table
          className={`${styles.table} ${styles.paymentLogTable} ${styles.wideScrollTable}`}
          data-testid="application-signature-payment-tab-payment-log-table"
        >
          <thead>
            <tr>
              <th>Type</th>
              <th>Method</th>
              <th>Base Fee</th>
              <th>Service Fee</th>
              <th>Total</th>
              <th>Log ID</th>
              <th>Transaction</th>
              <th>IP Address</th>
              <th>Date / Time</th>
              <th>Status</th>
              <th>Gateway Status</th>
              <th>Paid By</th>
              {canPerformPaymentActions && (
                <th className={styles.paymentLogActionHeader}>Action</th>
              )}
            </tr>
          </thead>
          <tbody>
            {paymentLogRowsForCurrentApplicant.map((payment, index) =>
              renderRow(payment, index, paymentLogRowsForCurrentApplicant),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
